import {
  doc,
  getDocs,
  collection,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { firebaseFirestore } from '@/infrastructure/firebase/firebaseFirestore.js';
import { type MessageEvent, type ValidationResult } from '@k-ucap/shared';
import { getCurrentEnvironment, buildConverter, toTimestampString } from './firestoreUtils.js';

const MESSAGE_EVENTS = 'messageEvents';

const converter = buildConverter<MessageEvent>(
  (data) =>
    ({
      ...data,
      timestamp: toTimestampString(data.timestamp) ?? new Date().toISOString(),
      createdAt: toTimestampString(data.createdAt) ?? new Date().toISOString(),
    }) as MessageEvent,
  (model) => ({ ...model, createdAt: serverTimestamp() })
);

export class FirestoreMessageEventRepository {
  async findAll(): Promise<MessageEvent[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, MESSAGE_EVENTS),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('timestamp', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findByProjectId(projectId: string): Promise<MessageEvent[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, MESSAGE_EVENTS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('timestamp', 'desc'),
      limit(100)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async findByRunId(runId: string): Promise<MessageEvent[]> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const q = query(
      collection(db, MESSAGE_EVENTS),
      where('runId', '==', runId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('timestamp', 'desc'),
      limit(1000)
    ).withConverter(converter);
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data());
  }

  async save(event: MessageEvent): Promise<MessageEvent> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    const data = { ...event, environment: getCurrentEnvironment() };
    await setDoc(doc(db, MESSAGE_EVENTS, event.id).withConverter(converter), data);
    return data;
  }

  async saveValidation(id: string, _result: ValidationResult): Promise<void> {
    const db = firebaseFirestore;
    if (!db) throw new Error('Firestore not initialized');
    await updateDoc(doc(db, MESSAGE_EVENTS, id), { validation: _result.severity, updatedAt: serverTimestamp() });
  }

  subscribeToProject(projectId: string, callback: (events: MessageEvent[]) => void): () => void {
    const db = firebaseFirestore;
    if (!db) {
      callback([]);
      return () => {};
    }
    const q = query(
      collection(db, MESSAGE_EVENTS),
      where('projectId', '==', projectId),
      where('environment', '==', getCurrentEnvironment()),
      orderBy('timestamp', 'desc'),
      limit(100)
    ).withConverter(converter);
    return onSnapshot(
      q,
      (snap) => {
        callback(snap.docs.map((d) => d.data()));
      },
      (err) => {
        console.error('Firestore messageEvents listener error:', err);
        callback([]);
      }
    );
  }
}

export const createFirestoreMessageEventRepository = (): FirestoreMessageEventRepository =>
  new FirestoreMessageEventRepository();
